import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import { Grid } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import CommonLayout from '../components/CommonLayout';
import PermissionsManager from '../components/PermissionsManager';
import LoadingSubmitButton from '../components/LoadingSubmitButton';
import InputWithIcon from '../components/InputWithIcon';
import DropdownWithIcon from '../components/DropdownWithIcon';
import PageTitle from '../components/PageTitle';
import userIcon from '../assets/users/user.svg';
import emailIcon from '../assets/users/email.svg';
import phoneIcon from '../assets/users/phone.svg';
import roleIcon from '../assets/users/role.svg';
import passwordIcon from '../assets/users/password.svg';
import { hospitalRoles } from '../config/hospitalRoles';
import { requiresEntraForNewUsers } from '../auth/capabilityPolicy';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#103C6E',
          color: 'white',
          '&:hover': {
            backgroundColor: '#0d3157',
          },
          '&:disabled': {
            backgroundColor: '#B9EDE7',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          textAlign: 'right',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: 'translate(-14px, -9px) scale(0.75)',
          '&[data-shrink="false"]': {
            transform: 'translate(14px, 16px) scale(1)',
          },
        },
      },
    },
  },
});

function AddUser() {
    const { user: loggedInUser, hospitals, authCapabilities } = useContext(UserContext);
    const invitationOnly = requiresEntraForNewUsers(authCapabilities);
    const [user, setUser] = useState({
      name: '',
      email: '',
      phone: '',
      password: '',
      isSuperAdmin: false,
      roles: []
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [formError, setFormError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
  
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const hospitalId = params.get('hospitalId');
      const departmentId = params.get('departmentId');
  
      if (hospitalId && departmentId) {
        setUser(prevUser => ({
          ...prevUser,
          roles: [{ hospital: hospitalId, department: departmentId, role: 'USER' }]
        }));
      } else if (hospitals && hospitals.length === 1) {
        setUser(prevUser => ({
          ...prevUser,
          roles: [{ hospital: hospitals[0]._id, role: 'USER' }]
        }));
      }
    }, [location.search, hospitals]);
  
    const handleSuperAdminChange = (value) => {
      setUser({ ...user, isSuperAdmin: value});
    }

    const handlePasswordChange = (e) => {
      setUser({ ...user, password: e.target.value });
      setPasswordError(confirmPassword && e.target.value !== confirmPassword ? 'סיסמאות לא תואמות' : '');
    };

    const handleConfirmPasswordChange = (e) => {
      setConfirmPassword(e.target.value);
      if (user.password && user.password.length < 6) {
        setPasswordError('סיסמה צריכה להכיל מינימום 6 תוים');
      } else {
        setPasswordError(user.password && e.target.value !== user.password ? 'סיסמאות לא תואמות' : '');
      }
    };

  const handleRoleChange = (index, hospital, department, r) => {
    const newRoles = user.roles.map((role, idx) => {
      if (idx === index) {
        return { ...role, 'hospital': hospital, 'department': department, 'role': r };
      }
      return role;
    });
    setUser({ ...user, roles: newRoles });
  };

  const addRole = () => {
    setUser({ ...user, roles: [...user.roles, { hospital: hospitals.length === 1? hospitals[0]._id: '', role: 'USER' }] });
  };

  const removeRole = index => {
    setUser({ ...user, roles: user.roles.filter((_, idx) => idx !== index) });
  };  

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
      setEmailError('נא הזן כתובת אימייל תקינה');
      return;
    }
    setEmailError('');

    if (invitationOnly && (user.isSuperAdmin || user.roles.length !== 1 || !user.roles[0].hospital || !user.roles[0].department)) {
      setFormError('יש לבחור הרשאה אחת הכוללת בית חולים ומחלקה. הרשאות נוספות ניתן להוסיף לאחר ההצטרפות.');
      return;
    }
    if (!invitationOnly && (user.password.length < 6 || user.password !== confirmPassword)) {
      setPasswordError(user.password.length < 6 ? 'סיסמה צריכה להכיל מינימום 6 תוים' : 'סיסמאות לא תואמות');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (invitationOnly) {
        const role = user.roles[0];
        await API.post('/api/users/add-by-email', {
          email: user.email,
          hospitalId: role.hospital,
          departmentId: role.department,
          role: role.role,
        });
      } else {
        await API.post('/api/users', user);
      }
      navigate('/users');
    } catch (error) {
      console.error('Adding user failed:', error);
      setFormError(error.response?.data?.error?.hebMessage || 'שליחת ההזמנה נכשלה');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommonLayout>
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <div className="add-user-container">
            <PageTitle title={invitationOnly ? 'הזמנת משתמש חדש' : 'משתמש חדש'} />
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {!invitationOnly && (
                  <Grid item xs={12}>
                    <InputWithIcon
                      icon={userIcon}
                      placeholder="שם"
                      value={user.name}
                      onChange={(e) => setUser({ ...user, name: e.target.value })}
                      required
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <InputWithIcon
                    icon={emailIcon}
                    placeholder="אימייל"
                    value={user.email}
                    onChange={(e) => {
                      setUser({ ...user, email: e.target.value });
                      if (emailError) {
                        setEmailError('');
                      }
                    }}
                    required
                    error={!!emailError}
                    helperText={emailError}
                    style={{ borderColor: emailError ? 'red' : '' }}
                  />
                  {emailError && 
                    <p style={{ color: 'red', fontSize: 'smaller' }}>{emailError}</p>
                  }
                </Grid>
                {!invitationOnly && (
                  <>
                    <Grid item xs={12}>
                      <InputWithIcon
                        icon={passwordIcon}
                        placeholder="סיסמה"
                        type="password"
                        value={user.password}
                        onChange={handlePasswordChange}
                        required
                        error={!!passwordError}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <InputWithIcon
                        icon={passwordIcon}
                        placeholder="חזור על הסיסמה"
                        type="password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                        error={!!passwordError}
                      />
                      {passwordError && <p style={{ color: 'red', fontSize: 'smaller' }}>{passwordError}</p>}
                    </Grid>
                    <Grid item xs={12}>
                      <InputWithIcon
                        icon={phoneIcon}
                        placeholder="טלפון"
                        value={user.phone}
                        onChange={(e) => setUser({ ...user, phone: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DropdownWithIcon
                        icon={roleIcon}
                        placeholder="תפקיד בבית חולים"
                        value={user.hospitalRole || ''}
                        onChange={(e) => setUser({ ...user, hospitalRole: e.target.value })}
                      >
                        {hospitalRoles.map((role) => (
                          <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                      </DropdownWithIcon>
                    </Grid>
                  </>
                )}
                {loggedInUser.isSuperAdmin && <PermissionsManager
                  roles={user.roles}
                  hospitals={hospitals}
                  loggedInSuperAdmin={!invitationOnly && loggedInUser.isSuperAdmin}
                  isSuperAdmin={user.isSuperAdmin}
                  onChangeSuperAdmin={handleSuperAdminChange}
                  onRoleChange={handleRoleChange}
                  onAddRole={addRole}
                  onRemoveRole={removeRole}
                />}
                {formError && (
                  <Grid item xs={12}>
                    <p style={{ color: 'red', textAlign: 'center' }}>{formError}</p>
                  </Grid>
                )}
                <Grid item xs={12} className="submit-button-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>                  
                  <LoadingSubmitButton
                    isLoading={isSubmitting}
                    text={invitationOnly ? 'שלח הזמנה' : 'צור'}
                    loadingText={invitationOnly ? 'שולח...' : 'יוצר...'}
                    style={{
                      backgroundColor: '#103C6E',
                      color: 'white',
                      minWidth: '200px',
                      '&:hover': {
                        backgroundColor: '#0d3157',
                      },
                      '&:disabled': {
                        backgroundColor: '#B9EDE7',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </div>
        </ThemeProvider>
      </CacheProvider>
    </CommonLayout>
  );
}

export default AddUser;